import { Link } from "react-router-dom";
import { mediaSrc } from "../api";

export function PageHeader({ overline, title, desc, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        {overline && <div className="overline mb-2">{overline}</div>}
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{title}</h1>
        {desc && <p className="text-slate-400 mt-1.5 max-w-2xl">{desc}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc, action }) {
  return (
    <div className="card p-12 flex flex-col items-center text-center" data-testid="empty-state">
      {Icon && <div className="h-14 w-14 rounded-full bg-white/5 flex items-center justify-center mb-4"><Icon className="text-slate-500" size={26} /></div>}
      <h3 className="font-heading font-semibold text-lg">{title}</h3>
      {desc && <p className="text-slate-400 text-sm mt-2 max-w-sm">{desc}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ProgressBar({ pct, className = "" }) {
  return (
    <div className={`h-2 rounded-full bg-white/10 overflow-hidden ${className}`}>
      <div className="h-full rounded-full bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8] transition-all duration-500" style={{ width: `${pct || 0}%` }} />
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, accent = "#F26522", testid }) {
  return (
    <div className="card p-5 flex items-center gap-4" data-testid={testid}>
      <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}22` }}>
        <Icon size={20} style={{ color: accent }} />
      </div>
      <div>
        <div className="font-heading text-2xl font-bold leading-none">{value}</div>
        <div className="text-slate-400 text-xs mt-1">{label}</div>
      </div>
    </div>
  );
}

const levelColors = {
  Beginner: "bg-emerald-500/15 text-emerald-400",
  Intermediate: "bg-amber-500/15 text-amber-400",
  Advanced: "bg-red-500/15 text-red-400",
  Expert: "bg-purple-500/15 text-purple-400",
};

export function LevelChip({ level }) {
  return <span className={`chip ${levelColors[level] || "bg-white/10 text-slate-300"}`}>{level}</span>;
}

export function StatusChip({ status }) {
  const map = {
    published: "bg-emerald-500/15 text-emerald-400",
    draft: "bg-amber-500/15 text-amber-400",
    archived: "bg-slate-500/15 text-slate-400",
    active: "bg-emerald-500/15 text-emerald-400",
    suspended: "bg-red-500/15 text-red-400",
    valid: "bg-emerald-500/15 text-emerald-400",
    revoked: "bg-red-500/15 text-red-400",
  };
  return <span className={`chip capitalize ${map[status] || "bg-white/10 text-slate-300"}`}>{status}</span>;
}

export function PathCard({ p, to }) {
  return (
    <Link to={to} className="card-hover overflow-hidden group block" data-testid={`path-card-${p.slug || p.id}`}>
      <div className="h-32 bg-[#1B2A4A] relative overflow-hidden">
        {p.cover_url && <img src={mediaSrc(p.cover_url)} alt={p.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#121C30] to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          {p.category && <span className="chip bg-black/40 backdrop-blur text-white">{p.category}</span>}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-heading font-semibold text-lg leading-tight group-hover:text-[#F26522] transition-colors">{p.title}</h3>
        <p className="text-slate-400 text-sm mt-1.5 line-clamp-2">{p.description}</p>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span>{p.completed_modules || 0}/{p.total_modules || 0} modules</span>
            <span>{p.progress_pct || 0}%</span>
          </div>
          <ProgressBar pct={p.progress_pct} />
        </div>
      </div>
    </Link>
  );
}
